import { call, put } from "redux-saga/effects";
import Swal from "sweetalert2";
import { API_BASE_URL, deleteRequest, extractErrorMessage, getRequest, postRequest, putRequest } from "../../../api";
import {
    payrollCalculateBulletinFail,
    payrollCalculateBulletinSuccess,
    payrollDeleteResourceFail,
    payrollDeleteResourceSuccess,
    payrollFetchBulletinLinesFail,
    payrollFetchBulletinLinesRequest,
    payrollFetchBulletinLinesSuccess,
    payrollFetchResourceFail,
    payrollFetchResourceRequest,
    payrollFetchResourceSuccess,
    payrollSaveResourceFail,
    payrollSaveResourceSuccess,
} from "../actions/payrollActions";

export const PAYROLL_ENDPOINTS = {
    exercicesPaie: "/api/exercices-paie",
    periodes: "/api/periodes-paie",
    historiqueSalaires: "/api/historique-salaires",
    rubriques: "/api/rubriques-paie",
    tauxCotisations: "/api/taux-cotisations",
    baremesIr: "/api/baremes-ir",
    baremesFraisProfessionnels: "/api/baremes-frais-professionnels",
    typesHeuresSupplementaires: "/api/types-heures-supplementaires",
    situationsCnss: "/api/situations-cnss-mensuelles",
    elementsVariables: "/api/elements-variables-paie",
    bulletins: "/api/bulletins-paie",
    remunerations: "/api/remunerations",
    parametragesPrimes: "/api/parametrages-primes",
};

const getToken = () => sessionStorage.getItem("token") || localStorage.getItem("token") || "";

const buildQuery = (params = {}) => {
    const pairs = Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "");
    if (pairs.length === 0) return "";
    return `?${pairs.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join("&")}`;
};

const getEndpoint = (resource) => {
    const endpoint = PAYROLL_ENDPOINTS[resource];
    if (!endpoint) throw new Error(`Ressource paie inconnue: ${resource}`);
    return endpoint;
};

export function* payrollFetchResourceSaga(action) {
    const { resource, params } = action.payload;
    try {
        const response = yield call(getRequest, {
            url: `${API_BASE_URL}${getEndpoint(resource)}${buildQuery(params)}`,
            token: getToken(),
        });
        yield put(payrollFetchResourceSuccess(resource, response.data || []));
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors du chargement.");
        yield put(payrollFetchResourceFail(resource, message));
    }
}

export function* payrollSaveResourceSaga(action) {
    const { resource, data, id } = action.payload;
    try {
        const url = `${API_BASE_URL}${getEndpoint(resource)}${id ? `/${id}` : ""}`;
        const response = id
            ? yield call(putRequest, { url, data, token: getToken() })
            : yield call(postRequest, { url, data, token: getToken() });

        yield put(payrollSaveResourceSuccess(resource, response.data));
        yield put(payrollFetchResourceRequest(resource));
        yield call([Swal, Swal.fire], {
            icon: "success",
            title: id ? "Modification enregistrée" : "Création enregistrée",
            timer: 1400,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de l'enregistrement.");
        yield put(payrollSaveResourceFail(resource, message));
        yield call([Swal, Swal.fire], { icon: "error", title: "Erreur", text: message });
    }
}

export function* payrollDeleteResourceSaga(action) {
    const { resource, id } = action.payload;
    try {
        yield call(deleteRequest, {
            url: `${API_BASE_URL}${getEndpoint(resource)}/${id}`,
            token: getToken(),
        });
        yield put(payrollDeleteResourceSuccess(resource, id));
        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "Suppression effectuee",
            timer: 1200,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la suppression.");
        yield put(payrollDeleteResourceFail(resource, message));
        yield call([Swal, Swal.fire], { icon: "error", title: "Erreur", text: message });
    }
}

export function* payrollCalculateBulletinSaga(action) {
    try {
        const response = yield call(postRequest, {
            url: `${API_BASE_URL}/api/bulletins-paie/calculate`,
            data: action.payload,
            token: getToken(),
        });
        yield put(payrollCalculateBulletinSuccess(response.data));
        yield put(payrollFetchResourceRequest("bulletins"));
        yield put(payrollFetchBulletinLinesRequest(response.data.id));
        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "Bulletin calcule",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors du calcul du bulletin.");
        yield put(payrollCalculateBulletinFail(message));
        yield call([Swal, Swal.fire], { icon: "error", title: "Calcul impossible", text: message });
    }
}

export function* payrollFetchBulletinLinesSaga(action) {
    try {
        const response = yield call(getRequest, {
            url: `${API_BASE_URL}/api/lignes-bulletins-paie?bulletinId=${encodeURIComponent(action.payload.bulletinId)}`,
            token: getToken(),
        });
        yield put(payrollFetchBulletinLinesSuccess(action.payload.bulletinId, response.data || []));
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors du chargement des lignes du bulletin.");
        yield put(payrollFetchBulletinLinesFail(message));
    }
}
